<template>
  <div>
    <div v-if="enoughDetailsToShow" class="flex -mx-5">
      <div class="flex-shrink px-5">
        <h4>Submitted Info</h4>
        <div class="mb-3">
          <p class="font-bold">
            Project Name
          </p>
          <p v-text="computedProject.name" />
        </div>
        <div v-if="computedProject.number" class="mb-3">
          <p class="font-bold">
            Project Number
          </p>
          <p v-text="computedProject.number" />
        </div>
        <div v-if="computedProject.type" class="mb-3">
          <p class="font-bold">
            Project Type
          </p>
          <p v-text="computedProject.type" />
        </div>
        <div v-if="computedProject.statusAtRegistration" class="mb-3">
          <p class="font-bold">
            Status at Registration
          </p>
          <p v-text="computedProject.statusAtRegistration" />
        </div>
        <div v-if="computedProject.city || computedProject.stateProvince || computedProject.country" class="mb-3">
          <p class="font-bold">
            Location
          </p>
          <p>
            {{ computedProject.city }}, {{ computedProject.stateProvince }}, {{ computedProject.country }}
          </p>
        </div>
        <div v-if="computedProject.standards" class="mb-3">
          <p class="font-bold">
            Standards
          </p>
          <p v-text="computedProject.standards" />
        </div>
        <div v-if="computedProject.projectBidDate" class="mb-3">
          <p class="font-bold">
            Project Bid Date
          </p>
          <p>
            {{ computedProject.projectBidDate | formatShortDate }}
          </p>
        </div>
        <div v-if="computedProject.estimatedShopDrawingStartDate" class="mb-3">
          <p class="font-bold">
            Estimated Start of Fabrication
          </p>
          <p>
            {{ computedProject.estimatedShopDrawingStartDate | formatShortDate }}
          </p>
        </div>
        <div v-if="computedProject.estimatedCompletionDate" class="mb-3">
          <p class="font-bold">
            Estimated Completion Start
          </p>
          <p>
            {{ computedProject.estimatedCompletionDate | formatShortDate }}
          </p>
        </div>
      </div>
      <div class="px-10 relative flex-grow">
        <div class="vertical-divider" />
      </div>
      <div class="w-5/12 px-5">
        <h4 class="px-8">
          Similar Projects
        </h4>
        <div v-if="loadingSimilars" class="text-center py-10">
          <d-loader />
          <p>Fetching similiar projects...</p>
        </div>
        <div v-else class="px-8">
          <div v-if="similars.length > 0">
            <div v-for="similar in similars" :key="similar.name" class="bg-white rounded border border-gray-500 p-5 cursor-pointer mb-4" @click="showCompareModal(similar)">
              <p class="font-bold text-lg">
                {{ similar.name }}
              </p>
              <p>
                #{{ similar.number }}
              </p>
              <p>
                {{ similar.city }}, {{ similar.stateProvince }}<span v-if="similar.country">, {{ similar.country }}</span>
              </p>
            </div>
          </div>
          <div v-else class="text-center">
            No projects could be found in Filemaker that matches this project name, number, city, and/or state. If this is incorrect, please edit this project information for us to pull in similar results.
          </div>
          <div class="word-separator my-10">
            Or
          </div>
          <div class="text-center">
            <d-btn @click="showManualProjectNumberPrompt">
              {{ computedProject.number ? 'Change' : 'Enter a' }} Project Number Manually
            </d-btn>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="mb-10">
      User responded that no project information was available
    </div>
  </div>
</template>

<script>
  import axios from 'axios';
  import { mapFilemakerProjectToProjectSubmission } from '@resources/scripts/services/projectRegistration.service';
  import CompareProjectModal from './CompareProjectModal.vue';

  export default {
    props: {
      project: {
        type: Object,
        default: () => ({}),
      },

      submissionSource: String,
    },

    data() {
      return {
        similars: [],
        loadingSimilars: true,
      };
    },

    computed: {
      computedProject: {
        get() {
          return this.project;
        },
        set(value) {
          this.$emit('update:project', value);
        },
      },

      enoughDetailsToShow() {
        return this.computedProject.name;
      },
    },

    mounted() {
      if (this.enoughDetailsToShow) {
        this.queryFileMakerSimilars();
      }
    },

    methods: {
      async queryFileMakerSimilars() {
        this.loadingSimilars = true;
        if (this.computedProject.number) {
          const { data: projectNumberResult } = await axios.post('/api/projects/query/paged?currentPage=1&pageSize=5', {
            'QCP Project Number Calc': this.computedProject.number,
          });
          const { data: projectNameResult } = await axios.post('/api/projects/query/paged?currentPage=1&pageSize=5', {
            'PR Name': this.computedProject.name,
          });
          const mappedprojectNumberResult = projectNumberResult.resultItems.map(firm => mapFilemakerProjectToProjectSubmission(firm, this.submissionSource));
          const mappedProjectNameResult = projectNameResult.resultItems.map(firm => mapFilemakerProjectToProjectSubmission(firm, this.submissionSource));
          this.similars = [...mappedprojectNumberResult, ...mappedProjectNameResult.filter(m => m.number !== this.computedProject.number)];
          this.loadingSimilars = false;
        } else {
          const { data } = await axios.post('/api/projects/query/paged?currentPage=1&pageSize=5', {
            'PR Name': this.computedProject.name,
            'QCP Project Number Calc': this.computedProject.number,
            'PR City': this.computedProject.city,
            'PR St': this.computedProject.stateProvince,
          });
          this.similars = data.resultItems.map(firm => mapFilemakerProjectToProjectSubmission(firm, this.submissionSource));
          this.loadingSimilars = false;
        }
      },

      showCompareModal(selectedSimilar) {
        this.$modal.open({
          parent: this,
          width: 900,
          component: CompareProjectModal,
          hasModalCard: true,
          props: {
            submittedInfo: this.computedProject,
            existingInfo: selectedSimilar,
          },
          events: {
            setRevisedInfo: (data) => {
              this.computedProject = data;
              this.$emit('save-changes', true);
            },
          },
        });
      },

      showManualProjectNumberPrompt() {
        this.$dialog.prompt({
          message: 'Type in the Project Number provided by the customer or from Filemaker',
          inputAttrs: {
            placeholder: 'Project Number',
          },
          onConfirm: (value) => {
            this.computedProject.number = value;
            this.$emit('save-changes', true);
            this.$toast.success('The project information has been updated');
            this.queryFileMakerSimilars();
          },
        });
      },
    },
  };
</script>